import { useCallback, useEffect, useState } from 'react';
import usePayment from 'state/payment/hooks/usePayment';
import CURRENCY from 'utils/currency';
import { PAYMENTS } from 'state/payment/type';
import {
  TransactionTableData,
  TransactionFetchParams,
  UseTransactionDetailsReturn,
} from '../types';

interface UseTransactionDetailsParams {
  dateFrom: string;
  dateTo: string;
  isActiveTab: boolean;
  urlParams: URLSearchParams;
  triggerRefresh: boolean;
}

/**
 * Preprocess transaction data to add computed fields
 */
function preprocessTransactionData(
  data: PAYMENTS.ITransactionResult[],
  isGatewayTransactionID: (id: string) => boolean
): TransactionTableData[] {
  return data.map(
    (row) =>
      ({
        ...row,
        customerName:
          !row.customer_first_name && !row.customer_last_name
            ? ''
            : row.customer_first_name === row.customer_last_name
              ? row.customer_first_name
              : `${row.customer_first_name || ''} ${row.customer_last_name || ''}`.trim(),
        grossAmount: isGatewayTransactionID(row.id)
          ? CURRENCY.getGrossAmount(
              row?.gross_receipt_paid_calculated,
              row.gross_receipt_paid,
              row.order_amount
            )
          : row.charge_amount || row?.calculated_total,
        transactionDate: row.order_create_date || row?.create_date,
      }) as TransactionTableData
  );
}

export function useTransactionDetails({
  dateFrom,
  dateTo,
  isActiveTab,
  urlParams,
  triggerRefresh,
}: UseTransactionDetailsParams): UseTransactionDetailsReturn {
  const [prevKey, setPrevKey] = useState<string | null>(null);
  const [nextKey, setNextKey] = useState<string | null>(null);
  const [numResults, setNumResults] = useState(0);

  const { payment, toFetchTransactions, isGatewayTransactionID } = usePayment();

  const amountOption = urlParams.get('condition') || 'equal';
  // Browser URL params are amount1/amount2 (see FILTER_PARAM_KEYS + every other
  // consumer); the underscore form (amount_1) is only the outgoing gateway param
  // name, applied in buildFetchParams below. Reading amount_1 here dropped the value.
  const firstAmount = urlParams.get('amount1') || undefined;
  const secondAmount = urlParams.get('amount2') || undefined;
  const statusArray = urlParams.get('status')?.split(',') || [];
  const paymentTypeArray = urlParams.get('payment_type')?.split(',') || [];
  const storeIdArray = urlParams.get('selected_entity_ids')?.split(',') || [];
  const searchString = urlParams.get('search') || undefined;

  const buildFetchParams = useCallback(
    (additionalParams?: Partial<TransactionFetchParams>): TransactionFetchParams => ({
      start_date: dateFrom,
      end_date: dateTo,
      amount_1: firstAmount,
      amount_2: secondAmount,
      condition: amountOption || undefined,
      status: statusArray.length > 0 ? statusArray.join(',') : undefined,
      payment_type: paymentTypeArray.length > 0 ? paymentTypeArray.join(',') : undefined,
      selected_entity_ids: storeIdArray.length > 0 ? storeIdArray.join(',') : undefined,
      search_string: searchString,
      ...additionalParams,
    }),
    [
      dateFrom,
      dateTo,
      firstAmount,
      secondAmount,
      amountOption,
      statusArray,
      paymentTypeArray,
      storeIdArray,
      searchString,
    ]
  );

  const fetchTransactions = useCallback(
    (additionalParams?: Partial<TransactionFetchParams>) => {
      toFetchTransactions(buildFetchParams(additionalParams));
    },
    [buildFetchParams, toFetchTransactions]
  );

  const prevPage = useCallback(() => {
    toFetchTransactions({
      key: prevKey,
      ...buildFetchParams(),
    });
  }, [prevKey, buildFetchParams, toFetchTransactions]);

  const nextPage = useCallback(() => {
    toFetchTransactions({
      key: nextKey,
      ...buildFetchParams(),
    });
  }, [nextKey, buildFetchParams, toFetchTransactions]);

  // Update pagination state when transactions change
  useEffect(() => {
    if (payment.transactions?.items?.length > 0) {
      setNextKey(payment.transactions.next_key);
      setPrevKey(payment.transactions.prev_key);
      setNumResults(payment.transactions.total_count);
    } else {
      setNumResults(0);
    }
  }, [payment.transactions]);

  // Fetch transactions when filters or dates change
  useEffect(() => {
    if (isActiveTab) {
      fetchTransactions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, triggerRefresh, isActiveTab, dateFrom, dateTo]);

  const tableData = preprocessTransactionData(
    payment.transactions?.items || [],
    isGatewayTransactionID
  );

  return {
    tableData,
    // isLoading is a counter, not a flag: reset()-on-unmount can zero it while
    // requests are still in flight, so a later decrement drives it negative.
    // Boolean(negative) is truthy and would keep the table stuck "loading" with
    // data hidden — compare against 0 instead.
    isLoading: payment.isLoading > 0,
    isEmpty: !(payment?.transactions?.items?.length > 0),
    prevKey,
    nextKey,
    numResults,
    prevPage,
    nextPage,
    fetchTransactions,
  };
}
